import React, { useEffect, useState } from 'react';
import CallController from '../API/CallController';
import ConfirmModal from './ConfirmModal';
import '../scss/ManageProducts.scss';
import LinkifiedText from './LinkifiedText';

interface Category {
  id: number;
  name: string;
  parentCategoryId?: number | null;
  children?: Category[]; // For tree structure
}

interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  imageUrl: string;
  categoryId: number;
  priceUnit: string;
}

const ManageProducts = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [editingProductId, setEditingProductId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<Product>>({});
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null); // For delete confirmation modal
  const [openCategories, setOpenCategories] = useState<number[]>([]);

  const token = localStorage.getItem('token');

  // toggle category
  const toggleCategory = (id: number) => {
    setOpenCategories(prev =>
      prev.includes(id)
        ? prev.filter(c => c !== id)
        : [...prev, id]
    );
  };

  // Fetch categories and products on component mount
  useEffect(() => {
    fetch(CallController.GetAllCategories)
      .then(res => res.json())
      .then(data => setCategories(data))
      .catch(err => console.error('Error fetching categories:', err));

    fetch(CallController.GetAllProducts)
      .then(res => res.json())
      .then(data => setProducts(data))
      .catch(err => console.error('Error fetching products:', err));
  }, []);

  // Build a tree structure from flat category list, sorting children alphabetically
  const buildCategoryTree = (cats: Category[], parentId: number | null = null): Category[] => {
    return cats
      .filter(cat => (cat.parentCategoryId ?? null) === parentId)
      .sort((a, b) => a.name.localeCompare(b.name, 'sv')) // Sorterar huvud- & underkategorier
      .map(cat => ({
        ...cat,
        children: buildCategoryTree(cats, cat.id),
      }));
  };

  const categoryTree = buildCategoryTree(categories);

  // Start editing a product
  const startEdit = (product: Product) => {
    setEditingProductId(product.id);
    setEditForm({ ...product });
  };

  // Update edit form state
  const handleEditChange = (field: keyof Product, value: any) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  // Save edited product
  const saveEdit = async () => {
    if (!editingProductId) return;

    try {
      const formData = new FormData();
      formData.append('name', editForm.name || '');
      formData.append('description', editForm.description || '');
      formData.append('price', (editForm.price || 0).toString());
      formData.append('priceUnit', editForm.priceUnit || 'kr');
      formData.append('categoryId', (editForm.categoryId || 0).toString());

      if (imageFile) {
        formData.append('image', imageFile);
      }

      const res = await fetch(CallController.UpdateProduct(editingProductId), {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('Server error:', errorText);
      }

      const updatedProduct = await res.json();

      setProducts(prev =>
        prev.map(p => (p.id === editingProductId ? updatedProduct : p))
      );

      setEditingProductId(null);
      setEditForm({});
      setImageFile(null);
      alert('Product updated successfully!');
    } catch (err) {
      alert(`Error updating product: ${(err as Error).message}`);
    }
  };

  // Cancel editing mode
  const cancelEdit = () => {
    setEditingProductId(null);
    setEditForm({});
  };

  // Request delete confirmation
  const deleteProduct = (id: number) => {
    setConfirmDeleteId(id);
  };

  // Delete product after confirmation
  const deleteProductConfirmed = async (id: number) => {
    try {
      const res = await fetch(CallController.DeleteProductById(id), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText);
      }

      setProducts(prev => prev.filter(p => p.id !== id));
      setConfirmDeleteId(null);
      alert('Product deleted successfully!');
    } catch (err) {
      alert(`Error deleting product: ${(err as Error).message}`);
      setConfirmDeleteId(null);
    }
  };

  // Render products for a category (sorted)
  const renderProducts = (categoryId: number) => {
    const prods = products
      .filter(p => p.categoryId === categoryId)
      .sort((a, b) => a.name.localeCompare(b.name, 'sv'));

    if (prods.length === 0) return <em>Inga produkter</em>;

    return prods.map(product => (
      <div key={product.id} className="product-item">
        {editingProductId === product.id ? (
          <div className="edit-form">
            <input
              type="text"
              value={editForm.name || ''}
              onChange={e => handleEditChange('name', e.target.value)}
              placeholder="Namn"
            />
            <textarea
              value={editForm.description || ''}
              onChange={e => handleEditChange('description', e.target.value)}
              placeholder="Beskrivning"
            />
            <div className="price-unit-wrapper">
              <input
                type="number"
                value={editForm.price !== undefined ? editForm.price : ''}
                onChange={e => handleEditChange('price', e.target.value === '' ? undefined : Number(e.target.value))}
                placeholder="Pris"
              />
              <select
                value={editForm.priceUnit || 'kr'}
                onChange={e => handleEditChange('priceUnit', e.target.value)}
              >
                <option value="kr">kr</option>
                <option value="kr/kg">kr/kg</option>
                <option value="kr/g">kr/g</option>
                <option value="kr/st">kr/st</option>
                <option value="kr/l">kr/l</option>
              </select>
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={e => setImageFile(e.target.files?.[0] || null)}
            />
            {editForm.imageUrl && (
              <div className="current-image">
                <p>Nuvarande bild:</p>
                <img
                  src={`https://accordi-web-eqb2byhxd0feffhu.norwayeast-01.azurewebsites.net${editForm.imageUrl}`}
                  alt="Product"
                  style={{ width: '100px', borderRadius: '4px', marginBottom: '0.5rem' }}
                />
              </div>
            )}
            <select
              value={editForm.categoryId || categoryId}
              onChange={e => handleEditChange('categoryId', Number(e.target.value))}
            >
              <option value="">Välj underkategori</option>
              {categories
                .filter(cat => cat.parentCategoryId !== null)
                .sort((a, b) => a.name.localeCompare(b.name, 'sv')) // Sorterar underkategori i edit
                .map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
            </select>
            <div className="edit-buttons">
              <button onClick={saveEdit}>Spara</button>
              <button onClick={cancelEdit}>Avbryt</button>
            </div>
          </div>
        ) : (
          <>
            <div className="product-info">
              <strong>{product.name}</strong>
              <p className="product-description"><LinkifiedText text={product.description} /></p>
              <div className="price">{product.price} {product.priceUnit}</div>
            </div>
            <div className="product-actions">
              <button onClick={() => startEdit(product)}>Redigera</button>
              <button onClick={() => deleteProduct(product.id)}>Radera</button>
            </div>
          </>
        )}
      </div>
    ));
  };

  // Render category tree recursively (underkategorier sorterade via buildCategoryTree)
  const renderCategoryTree = (cats: Category[]) => {
    return cats.map(cat => {
      const isOpen = openCategories.includes(cat.id);

      return (
        <div key={cat.id} className="category-block">
          <h3
            className="category-header"
            onClick={() => toggleCategory(cat.id)}
          >
            {isOpen ? "▼" : "▶"} {cat.name}
          </h3>

          {isOpen && (
            <>
              {renderProducts(cat.id)}

              {cat.children && cat.children.length > 0 && (
                <div className="subcategory-list">
                  {renderCategoryTree(cat.children)}
                </div>
              )}
            </>
          )}
        </div>
      );
    });
  };
  return (
    <div className="manage-products">
      <h2>Hantera Produkter</h2>
      {categories.length === 0 && <p>Inga nuvarande kategorier.</p>}
      {products.length === 0 && <p>Inga nuvarande produkter.</p>}
      {renderCategoryTree(categoryTree)}

      {confirmDeleteId !== null && (
        <ConfirmModal
          message="Do you want to delete the product?"
          onConfirm={() => deleteProductConfirmed(confirmDeleteId)}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </div>
  );
};

export default ManageProducts;